import { inject, Injectable } from '@angular/core';
import { DefaultService } from '@codegen/service';
import { ApplicationStatusResponse } from '@codegen';
import { Observable, of } from 'rxjs';
import { finalize, shareReplay, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class StatusCacheService {
  private readonly defaultService = inject(DefaultService);
  private pendingRequests = new Map<
    string,
    Observable<ApplicationStatusResponse>
  >();
  private statusCache = new Map<
    string,
    { data: ApplicationStatusResponse; timestamp: number }
  >();
  private readonly CACHE_TTL = 500;

  public getStatus(
    applicationId: string
  ): Observable<ApplicationStatusResponse> {
    const cached = this.statusCache.get(applicationId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return of(cached.data);
    }

    const pending = this.pendingRequests.get(applicationId);
    if (pending) {
      return pending;
    }

    const request$ = this.defaultService.status({ applicationId }).pipe(
      tap(response => {
        this.statusCache.set(applicationId, {
          data: response,
          timestamp: Date.now(),
        });
      }),
      finalize(() => {
        this.pendingRequests.delete(applicationId);
      }),
      shareReplay(1)
    );

    this.pendingRequests.set(applicationId, request$);
    return request$;
  }

  public clearCache(applicationId?: string): void {
    if (applicationId) {
      this.statusCache.delete(applicationId);
      this.pendingRequests.delete(applicationId);
    } else {
      this.statusCache.clear();
      this.pendingRequests.clear();
    }
  }
}
